/**
 * Envoltorio visual ÚNICO de los emails de campaña.
 *
 * Antes había varios envoltorios distintos y por eso, al generar con IA, el
 * email "cambiaba de maqueta entera" en vez de solo cambiar el texto: la
 * plantilla predefinida usaba un `wrap()` propio (índigo, `<div>` de
 * maquetación, `border-radius`/`box-shadow`) y el generador de IA construía
 * en el backend un documento completo totalmente distinto (azul de marca,
 * tablas planas). Ahora existe UN SOLO sitio que produce el documento final:
 * este módulo. Tanto `getDefaultTemplate()`/`getCampaignTemplate()` (plantillas
 * predefinidas) como la respuesta del generador de IA
 * (`POST /email-campaigns/generate-ai` y `/admin/campaigns/generate-ai`)
 * entregan SOLO el fragmento de contenido; quien lo envuelve siempre es
 * `wrapEmailContent()` de aquí, en `EmailCampaigns.tsx` y `AdminCampaigns.tsx`.
 * `VisualEmailEditor.tsx` también delega en `wrapEmailContent()` (con los
 * marcadores `<!--rm-content-start/end-->` como `content`) al re-envolver
 * tras cada edición: no tiene su propio envoltorio.
 *
 * Por qué vive en el dashboard y no en el backend: el envoltorio es cromo de
 * AUTORÍA (se aplica una vez, al elegir plantilla o generar con IA; a partir
 * de ahí el usuario lo edita a mano y se guarda tal cual en
 * `EmailCampaign.htmlContent`), no de envío — el pipeline de envío
 * (`runCampaignSending`/`sendCampaignTest`) nunca re-envuelve el HTML
 * persistido. Elegir una plantilla tampoco puede depender de una llamada de
 * red. Sin paquete compartido entre backend y dashboard, el sitio natural de
 * "cromo de autoría en español" es la superficie de autoría: el dashboard.
 *
 * La celda de contenido lleva `data-rm-content-cell="1"` (invisible para
 * cualquier cliente de correo, es un atributo `data-*` normal): es el
 * fallback que usa `EmailBlockEditor.tsx` (editor de bloques nuevo de
 * `EmailCampaigns.tsx`) para encontrar el fragmento editable en CUALQUIER
 * documento que produzca esta función, aunque todavía no lleve los
 * marcadores `<!--rm-content-start/end-->` (plantillas recién elegidas o
 * contenido recién generado con IA, antes de la primera edición). No lo usa
 * `VisualEmailEditor.tsx` (sigue con su propia cascada de `extractBodyContent`,
 * sin tocar), es aditivo y no cambia el HTML visible en ningún cliente.
 *
 * ⚠️ `BRAND_COLORS` DEBE COINCIDIR con el mismo objeto en
 * `reservamanager-backend/src/modules/email-campaigns/ai-service.ts` (su
 * `systemPrompt` interpola esos hex en las reglas del botón y de las
 * secciones destacadas para que lo que genera la IA case con esta cabecera).
 * Si una copia cambia y la otra no, el botón que genera la IA dejará de
 * coincidir con la cabecera del envoltorio.
 */

export type EmailLayoutVariant = "restaurant" | "admin";

export const BRAND_COLORS = {
  /** primary-500 — banda de cabecera. */
  primary: "#0ea5e9",
  /** primary-600 — botones, enlaces y filetes de acento. */
  primaryDark: "#0284c7",
  /** primary-100 — texto secundario sobre la banda de cabecera. */
  primaryLight: "#e0f2fe",
} as const;

/** Evita que un nombre de organización (o cualquier texto interpolado en el
 * envoltorio) con `&`/`<`/`>`/`"` rompa el HTML. */
export function escapeHtmlEntities(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isHttpUrl(url: string | null | undefined): url is string {
  return !!url && /^https?:\/\//i.test(url.trim());
}

interface WrapEmailContentOptions {
  /** "restaurant": campañas de un hostelero a sus clientes (cabecera con el
   * nombre del NEGOCIO, pie con {{unsubscribeUrl}}). "admin": campañas de la
   * plataforma a hosteleros (cabecera/pie de ReservaManager, sin enlace de
   * baja: ese flujo es otro). */
  variant: EmailLayoutVariant;
  /** Nombre del NEGOCIO. Se ignora en variant "admin" (siempre "ReservaManager"). */
  brandName: string;
  /** Solo se pinta si es una URL http(s) (nunca `data:` — evita reabrir la
   * puerta a logos en base64 incrustados, que es justo lo que recortaba los
   * emails en Gmail). Irrelevante en variant "admin". */
  logoUrl?: string | null;
  /** Fragmento HTML que va dentro de la celda de contenido
   * (`<td style="padding:32px 24px;">`). Nunca un documento completo. */
  content: string;
}

/**
 * Envuelve un fragmento de contenido con el envoltorio visual único: tablas
 * planas, sin `<style>`, sin `border-radius`/`box-shadow`/`overflow`, ancho
 * fluido (`width="100%"` + `max-width:600px`) con ghost table `[if mso]` para
 * que Outlook de escritorio no lo estire a todo el ancho, paleta
 * `BRAND_COLORS`, cabecera con el nombre del NEGOCIO (o de la plataforma en
 * variant "admin") y logo solo si es URL http(s).
 */
export function wrapEmailContent({
  variant,
  brandName,
  logoUrl,
  content,
}: WrapEmailContentOptions): string {
  const isAdmin = variant === "admin";
  const safeBrand = escapeHtmlEntities(
    brandName?.trim() || (isAdmin ? "ReservaManager" : "Tu negocio"),
  );
  const title = isAdmin ? "ReservaManager" : safeBrand;
  const year = new Date().getFullYear();

  const headerHtml = isAdmin
    ? `<span style="font-size:20px;font-weight:bold;color:#ffffff;">ReservaManager</span><br>
<span style="font-size:13px;color:${BRAND_COLORS.primaryLight};">La forma más fácil de gestionar tu restaurante</span>`
    : isHttpUrl(logoUrl)
      ? `<img src="${escapeHtmlEntities(logoUrl.trim())}" width="140" alt="${safeBrand}" style="display:block;margin:0 auto 8px;max-width:140px;height:auto;border:0;">
<span style="font-size:20px;font-weight:bold;color:#ffffff;">${safeBrand}</span>`
      : `<span style="font-size:20px;font-weight:bold;color:#ffffff;">${safeBrand}</span>`;

  // Variante "admin" NO lleva {{unsubscribeUrl}}: ese flujo (bajas de
  // clientes de restaurante) es otro y no aplica a hosteleros.
  const footerHtml = isAdmin
    ? `¿Necesitas ayuda? Escríbenos a <a href="mailto:reservamanager@lucrapp.com" style="color:#555555;text-decoration:underline;">reservamanager@lucrapp.com</a><br>
&copy; ${year} ReservaManager<br>
<a href="https://reservamanager.com" style="color:#777777;text-decoration:underline;">Web</a> &bull; <a href="https://dashboard.reservamanager.com" style="color:#777777;text-decoration:underline;">Dashboard</a>`
    : `&copy; ${year} ${safeBrand}<br>
<a href="{{unsubscribeUrl}}" style="color:#777777;text-decoration:underline;">Cancelar suscripción</a>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1.0">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${title}</title>
</head>
<body style="margin:0;padding:0;background-color:#f4f4f5;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" bgcolor="#f4f4f5" style="background-color:#f4f4f5;">
<tr>
<td align="center" style="padding:24px 12px;">

<!--[if mso]><table role="presentation" width="600" border="0" cellpadding="0" cellspacing="0"><tr><td><![endif]-->
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" bgcolor="#ffffff" style="width:100%;max-width:600px;background-color:#ffffff;">

<tr>
<td bgcolor="${BRAND_COLORS.primary}" align="center" style="padding:24px;font-family:Arial,Helvetica,sans-serif;">
${headerHtml}
</td>
</tr>

<tr>
<td data-rm-content-cell="1" style="padding:32px 24px;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:24px;color:#333333;">
${content}
</td>
</tr>

<tr>
<td bgcolor="#f4f4f5" align="center" style="padding:20px 24px;border-top:1px solid #e5e5e5;font-family:Arial,Helvetica,sans-serif;font-size:12px;line-height:18px;color:#777777;">
${footerHtml}
</td>
</tr>

</table>
<!--[if mso]></td></tr></table><![endif]-->

</td>
</tr>
</table>
</body>
</html>`;
}
